import {
  breadcrumb,
  reportError,
  setReportUser,
  type ErrorLevel,
  type ErrorScope,
  type ReportOptions,
} from './errorReporter';

/**
 * Web build.
 *
 * Firebase Crashlytics — только для нативных сборок: webpack статически разбирает любой
 * `require()` (в отличие от Metro, который отсекает по платформе во время выполнения), поэтому
 * `crashReporting.ts` с его `require('@react-native-firebase/crashlytics')` для веба собрать
 * нельзя. Webpack берёт этот `.web.ts` первым (см. `resolve.extensions` в webpack.config.cjs).
 *
 * Раньше здесь стояли пустышки, и веб не сообщал о поломках никуда вовсе. Теперь пустышка только
 * одна — сам Crashlytics; всё остальное уходит на наш бэкенд ровно так же, как на телефоне
 * (OBS-2). Плюс в вебе можно то, чего мы намеренно не делаем на Hermes: поставить глобальные
 * перехватчики. `window.onerror` и `unhandledrejection` в браузере ничего не заклинивают, а
 * ловят как раз то, до чего не дотягивается граница ошибок React, — сбои в обработчиках событий,
 * в таймерах и в необработанных промисах.
 */

export function initCrashReporting(): void {
  try {
    const w = globalThis as unknown as {
      addEventListener?: (type: string, listener: (event: unknown) => void) => void;
      __vrmErrorHooksInstalled?: boolean;
    };
    if (!w.addEventListener || w.__vrmErrorHooksInstalled) {
      return;
    }
    w.__vrmErrorHooksInstalled = true;

    w.addEventListener('error', (event: unknown) => {
      const e = event as { error?: unknown; message?: string; filename?: string; lineno?: number };
      void reportError('app', e.error ?? e.message ?? 'window.onerror', {
        level: 'error',
        context: { источник: e.filename, строка: e.lineno },
      });
    });

    w.addEventListener('unhandledrejection', (event: unknown) => {
      const e = event as { reason?: unknown };
      void reportError('app', e.reason ?? 'unhandledrejection', { level: 'error' });
    });
  } catch {
    /* сообщение о поломке не имеет права быть поломкой */
  }
}

export function setCrashUser(userId: string | null): void {
  setReportUser(userId);
}

export function trace(scope: ErrorScope, message: string): void {
  breadcrumb(scope, message);
}

export function reportIssue(scope: ErrorScope, error: unknown, options?: ReportOptions): void {
  void reportError(scope, error, options);
}

export function recordJsError(error: unknown, context?: Record<string, string>): void {
  reportIssue('app', error, { level: 'error', context });
}

export type { ErrorLevel, ErrorScope, ReportOptions };
